import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { FiTrendingUp, FiClock, FiCheckCircle, FiRepeat } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import { Card, CardHeader, CardBody } from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { api, unwrap } from '../../api/client';
import { getProductNamesAll } from '../../api/products';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmt(v) {
  if (!v) return '₹0';
  if (v >= 1e7) return '₹' + (v / 1e7).toFixed(2) + ' Cr';
  if (v >= 1e5) return '₹' + (v / 1e5).toFixed(2) + ' L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(1) + ' K';
  return '₹' + v.toLocaleString('en-IN');
}

function fmtShort(v) {
  if (!v) return '₹0';
  if (v >= 1e7) return '₹' + (v / 1e7).toFixed(1) + 'Cr';
  if (v >= 1e5) return '₹' + (v / 1e5).toFixed(0) + 'L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(0) + 'K';
  return '₹' + v;
}

export default function RevenueDashboard() {
  const currentYear = new Date().getFullYear();
  const [activePid, setActivePid] = useState('ALL');
  const [showAllMonths, setShowAllMonths] = useState(false);
  const navigate = useNavigate();

  const { data: productsData } = useQuery({
    queryKey: ['products', 'names', 'all'],
    queryFn: getProductNamesAll,
  });

  const { data: dashData, isLoading: loadingDash, error: dashError } = useQuery({
    queryKey: ['revenue', 'dashboard', activePid],
    queryFn: async () => {
      if (activePid === 'ALL') {
        return unwrap(await api.get('/finance/revenue/dashboard'));
      } else {
        return unwrap(await api.get('/finance/revenue/dashboard/by-product', { params: { productName: activePid } }));
      }
    },
  });

  const { data: monthlyRows, isLoading: loadingMonthly, error: monthlyError } = useQuery({
    queryKey: ['revenue', 'monthly-by-product', currentYear, activePid],
    queryFn: async () => unwrap(await api.get('/finance/revenue/dashboard/monthly-by-product', { 
      params: { 
        year: currentYear,
        ...(activePid !== 'ALL' && { productName: activePid })
      } 
    })),
  });

  const { data: yearlyRows, isLoading: loadingYearly } = useQuery({
    queryKey: ['revenue', 'yearly-by-product', currentYear],
    queryFn: async () => unwrap(await api.get('/finance/revenue/dashboard/yearly-by-product', { params: { year: currentYear } })),
  });

  const isLoading = loadingDash || loadingMonthly;
  const error = dashError || monthlyError;

  if (isLoading) {
    return (
      <AppLayout title="Revenue" breadcrumb="Revenue">
        <LoadingSpinner label="Loading revenue data..." />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Revenue" breadcrumb="Revenue">
        <ErrorBanner error={error} title="Failed to load revenue data" />
      </AppLayout>
    );
  }

  const products = [{ id: 'ALL', name: 'All Products' }, ...(productsData || []).map(p => ({ id: p.name, name: p.name }))];
  const rowsToProcess = monthlyRows || [];

  const byMonth = new Map(MONTHS.map((_, i) => [i + 1, { month: i + 1, paid: 0, pending: 0, overdue: 0 }]));
  rowsToProcess.forEach(row => {
    const m = Number(row.month);
    if (byMonth.has(m)) {
      const item = byMonth.get(m);
      item.paid += Number(row.paidRevenue || 0);
      item.pending += Number(row.pendingRevenue || 0);
      item.overdue += Number(row.overdueRevenue || 0);
    }
  });

  const chartData = Array.from(byMonth.values()).map(d => ({
    name: SHORT[d.month - 1],
    Paid: d.paid,
    Pending: d.pending,
    Overdue: d.overdue,
    total: d.paid + d.pending + d.overdue
  }));

  const grandTotal = chartData.reduce((s, r) => s + r.total, 0);
  const totPaid = chartData.reduce((s, r) => s + r.Paid, 0);
  const totPending = chartData.reduce((s, r) => s + r.Pending, 0);
  const totOverdue = chartData.reduce((s, r) => s + r.Overdue, 0);
  const curMonthIndex = new Date().getMonth();
  const curMonthData = chartData[curMonthIndex];
  const curMonthTotal = curMonthData ? curMonthData.total : 0;
  
  const pct = grandTotal > 0 ? Math.round((totPaid / grandTotal) * 100) : 0;
  const activeMonthsCount = chartData.filter(d => d.total > 0).length;
  
  const maxIdx = chartData.reduce((maxI, item, idx, arr) => item.total > arr[maxI].total ? idx : maxI, 0);
  const maxTotal = chartData[maxIdx].total;

  return (
    <AppLayout title="Revenue Dashboard" breadcrumb="Finance > Revenue">
      <div className="space-y-6">
        <PageHeader
          title="Revenue Dashboard"
          description={`Comprehensive revenue tracking and product breakdown for ${currentYear}`}
          actions={
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
              Fiscal Year {currentYear}
            </div>
          }
        />

        {/* Top KPI Cards Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Revenue</span>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiTrendingUp className="text-lg" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900">{fmt(grandTotal)}</div>
            <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
              <span className="font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">↑ 12.4%</span> vs last year
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Collected Revenue</span>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiCheckCircle className="text-lg" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900">{fmt(totPaid)}</div>
            <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
              <span className="font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{pct}%</span> collection rate
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pending & Overdue</span>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiClock className="text-lg" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900">{fmt(totPending + totOverdue)}</div>
            <div className="mt-2 text-xs text-slate-500">
              {(dashData?.pendingInvoicesCount || 0) + (dashData?.overdueInvoicesCount || 0)} open invoices
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Month</span>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiRepeat className="text-lg" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-bold text-slate-900">{fmt(curMonthTotal)}</div>
            <div className="mt-2 text-xs text-slate-500">
              {MONTHS[curMonthIndex]} {currentYear}
            </div>
          </div>
        </div>

        {/* Product Yearly Revenue Breakdown Table Card */}
        <Card>
          <CardHeader
            title="Product Yearly Revenue"
            subtitle={`Total revenue breakdown by product for ${currentYear}`}
          />
          <CardBody className="p-0 overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider">
                  <th className="py-3 px-6">Product Name</th>
                  <th className="py-3 px-6 text-right">Paid</th>
                  <th className="py-3 px-6 text-right">Pending</th>
                  <th className="py-3 px-6 text-right">Overdue</th>
                  <th className="py-3 px-6 text-right text-blue-700 font-bold">Total Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingYearly ? (
                  <tr><td colSpan="5" className="py-6 text-center text-slate-500">Loading table data...</td></tr>
                ) : (yearlyRows || []).length === 0 ? (
                  <tr><td colSpan="5" className="py-6 text-center text-slate-500">No revenue data found.</td></tr>
                ) : (
                  (yearlyRows || []).map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/80 transition-colors text-slate-800">
                      <td className="py-3.5 px-6 font-semibold text-slate-900">{row.productName}</td>
                      <td className="py-3.5 px-6 text-right text-blue-600 font-semibold">{fmt(row.paidRevenue)}</td>
                      <td className="py-3.5 px-6 text-right text-slate-600">{fmt(row.pendingRevenue)}</td>
                      <td className="py-3.5 px-6 text-right text-slate-700">{fmt(row.overdueRevenue)}</td>
                      <td className="py-3.5 px-6 text-right font-bold text-slate-900 bg-slate-50/50">{fmt(row.totalRevenue)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>

        {/* Monthly Revenue Chart and Table Card */}
        <Card>
          <CardHeader
            title="Monthly Revenue Breakdown"
            subtitle={`Month-by-month revenue performance for ${currentYear}`}
            actions={
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-500 mr-1">Product:</span>
                <div className="flex gap-1.5 flex-wrap">
                  {products.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setActivePid(p.id)}
                      className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all ${
                        activePid === p.id
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            }
          />
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6">
              <div className="h-56 relative pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 11 }} tickFormatter={fmtShort} />
                    <RechartsTooltip 
                      cursor={{ fill: '#F1F5F9' }} 
                      contentStyle={{ backgroundColor: '#FFFFFF', borderColor: '#E5E7EB', color: '#0F172A', fontSize: '12px', borderRadius: '10px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                      formatter={(val) => [fmt(val)]}
                    />
                    <Bar dataKey="Paid" stackId="a" fill="#2563EB" />
                    <Bar dataKey="Pending" stackId="a" fill="#93C5FD" />
                    <Bar dataKey="Overdue" stackId="a" fill="#475569" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-col justify-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Year Total</div>
                  <div className="text-xl font-bold text-slate-900 mt-0.5">{fmt(grandTotal)}</div>
                </div>
                <div className="border-t border-slate-200/80 pt-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Peak Month</div>
                  <div className="text-sm font-bold text-slate-800 mt-0.5">{maxTotal > 0 ? `${SHORT[maxIdx]} — ${fmt(maxTotal)}` : '—'}</div>
                </div>
                <div className="border-t border-slate-200/80 pt-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Active Months</div>
                  <div className="text-sm font-bold text-slate-800 mt-0.5">{activeMonthsCount} of 12</div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-6">Month</th>
                    <th className="py-3 px-6 text-right">Paid</th>
                    <th className="py-3 px-6 text-right">Pending</th>
                    <th className="py-3 px-6 text-right">Overdue</th>
                    <th className="py-3 px-6 text-right text-blue-700 font-bold">Total</th>
                    <th className="py-3 px-6 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {chartData.slice(0, showAllMonths ? 12 : 5).map((d, i) => {
                    const mIndex = chartData.indexOf(d);
                    return (
                      <tr key={d.name} className="hover:bg-slate-50/80 transition-colors text-slate-800">
                        <td className="py-3.5 px-6 font-semibold text-slate-900">{MONTHS[mIndex]}</td>
                        <td className="py-3.5 px-6 text-right font-semibold text-blue-600">{d.Paid > 0 ? fmt(d.Paid) : '—'}</td>
                        <td className="py-3.5 px-6 text-right text-slate-600">{d.Pending > 0 ? fmt(d.Pending) : '—'}</td>
                        <td className="py-3.5 px-6 text-right text-slate-700">{d.Overdue > 0 ? fmt(d.Overdue) : '—'}</td>
                        <td className="py-3.5 px-6 text-right font-bold text-slate-900">{d.total > 0 ? fmt(d.total) : '—'}</td>
                        <td className="py-3.5 px-6 text-center">
                          <button
                            onClick={() => navigate(`/finance/invoices?month=${mIndex + 1}&year=${currentYear}`)}
                            className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Invoices
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center pt-2">
              <button
                onClick={() => setShowAllMonths(!showAllMonths)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors"
              >
                {showAllMonths ? 'Show Less Months' : 'View All 12 Months'}
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  );
}
